import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Minus } from "lucide-react";
import { ProductContext } from "../../@types/productInfo";

interface CartTableProps {
  cart: any[];
  onIncrement: (product: ProductContext) => void;
  onDecrement: (product: ProductContext) => void;
  onRemove: (product: ProductContext) => void;
  itemStocks: Record<string, number>;
  isUpdating: boolean;
  isDecreasing: boolean;
  fetchStockForAllItems: () => void;
  isRemoving: boolean;
  isBlack: boolean;
  isMachine: boolean;
  stockLoading: boolean;
}

const CartTable: React.FC<CartTableProps> = ({
  cart,
  onIncrement,
  onDecrement,
  onRemove,
  itemStocks,
  isUpdating,
  isDecreasing,
  fetchStockForAllItems,
  isRemoving,
  isBlack,
  isMachine,
  stockLoading,
}) => {
  const capitalizeFirstLetter = (str: string | null | undefined) => {
    if (!str) return "";
    return str.replace(/\b\w/g, (char) => char.toUpperCase());
  };

  useEffect(() => {
    fetchStockForAllItems();

    const handleBeforeUnload = () => {
      console.log("Page is being refreshed");
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const renderStockMessage = (sku: string) => {
    const stockLevel = itemStocks[sku];

    if (stockLevel === undefined) {
      return (
        <span className="text-gray-400">Stock information unavailable</span>
      );
    }

    return (
      <div
        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
          stockLevel > 0 ? "bg-green-100 text-green-800" : ""
        }`}>
        {stockLevel > 0 ? (
          <>
            <span className="w-2 h-2 rounded-full bg-green-500 mr-1.5"></span>{" "}
            In Stock
          </>
        ) : (
          <></>
        )}
      </div>
    );
  };

  const isOutOfStock = (item: any) => {
    return itemStocks[item.sku] === 0;
  };

  const getImageSource = (item: any) => {
    return item.image;
  };

  const formatPrice = (price: number | null | undefined) => {
    if (price == null) return "N/A";
    return price.toLocaleString("en-ZA", {
      style: "currency",
      currency: "ZAR",
    });
  };


  return (
    <div className="overflow-x-auto rounded-lg shadow-sm">
      <table className="w-full text-sm text-left text-gray-700">
        <thead className="text-18 uppercase bg-gray-100">
          <tr>
            <th scope="col" className="px-6 py-3 text-gray-600 font-medium">
              Product
            </th>
            <th scope="col" className="px-6 py-3 text-gray-600 font-semibold">
              Name
            </th>
            <th scope="col" className="px-6 py-3 text-gray-600 font-medium">
              Price
            </th>
            <th scope="col" className="px-6 py-3 text-gray-600 font-medium">
              Quantity
            </th>
            <th scope="col" className="px-6 py-3 text-gray-600 font-medium">
              Total
            </th>
            <th scope="col" className="px-6 py-3 text-gray-600 font-medium">
              Action
            </th>
          </tr>
        </thead>

        <tbody>
          <AnimatePresence>
            {cart.map((item) => (
              <motion.tr
                key={item.id}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                className="bg-white border-b hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4">
                  <div className="relative">
                    <div className="flex-shrink-0 relative w-24 aspect-square">
                      <img
                        className="w-full h-full object-contain rounded-lg"
                        src={getImageSource(item)}
                        alt={capitalizeFirstLetter(item.name)}
                      />
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-col">
                    <span className="text-gray-900 text-base font-semibold">
                      {capitalizeFirstLetter(item.name)}
                    </span>
                    {item.sku && (
                      <span className="text-xs">
                        {renderStockMessage(item.sku)}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 text-gray-900 font-medium text-base">
                  {formatPrice(item.price)}
                </td>
                <td className="px-6 py-4 text-base">
                  <div className="flex items-center space-x-3">
                    <motion.button
                      type="button"
                      onClick={() => onDecrement(item)}
                      disabled={item.item_quantity <= 1}
                      whileTap={{ scale: 0.9 }}
                      className={`border border-[#080A40] ${
                        item.item_quantity <= 1
                          ? "bg-gray-100 cursor-not-allowed text-gray-400"
                          : "bg-white text-[#080A40] hover:bg-gray-100"
                      } rounded-full h-9 w-9 flex items-center justify-center transition-colors`}>
                      {isDecreasing ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <span className="text-lg">-</span>
                      )}
                    </motion.button>

                    <input
                      type="text"
                      className="w-16 text-center text-gray-900 border-0 bg-transparent text-lg"
                      value={item.item_quantity}
                      readOnly
                    />
                    <motion.button
                      type="button"
                      onClick={() => onIncrement(item)}
                      disabled={isOutOfStock(item)}
                      whileTap={{ scale: 0.9 }}
                      className={`border border-[#080A40] ${
                        isOutOfStock(item)
                          ? "bg-gray-100 cursor-not-allowed text-gray-400"
                          : "bg-white text-[#080A40] hover:bg-gray-100"
                      } rounded-full h-9 w-9 flex items-center justify-center transition-colors`}>
                      {isUpdating ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <span className="text-lg">+</span>
                      )}
                    </motion.button>
                  </div>
                </td>
                <td className="text-base px-6 py-4 text-gray-900 font-medium">
                  {formatPrice(
                    item.price && item.item_quantity
                      ? item.price * item.item_quantity
                      : null
                  )}
                </td>
                <td className="px-6 py-4 text-base">
                  <motion.button
                    type="button"
                    onClick={() => onRemove(item)}
                    whileTap={{ scale: 0.9 }}
                    className="p-3 rounded-full text-gray-500 hover:bg-gray-100 hover:text-red-500 transition-colors">
                    {isRemoving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <span className="text-2xl">×</span>
                    )}
                  </motion.button>
                </td>
              </motion.tr>
            ))}
          </AnimatePresence>
        </tbody>
      </table>
    </div>
  );
};

export default CartTable;
